import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import withRouter from "../../withRouter.js";
import {
  clientname,
  currentTenant,
  logo,
  userid,
} from "../../Common/Credentials.js";
import { spin } from "../../Common/NewLoader.jsx";
import { inventoryid } from "../../Common/Permissions.js";
import InventoryService from "../InventoryService.js";
import { NumericFormat } from "react-number-format";
import { IconButton, Tooltip, TextField, MenuItem } from "@mui/material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import Swal from "sweetalert2";

import DeleteIcon from "../../images/Icons/delete.png";
import EditIcon from "../../images/Icons/edit.png";
import ViewIcon from "../../images/Icons/preview.png";
import Breadcrumb from "../../NewUI/NewComponents/Breadcrumb.jsx";

const ServiceDataTable = ({ navigate, location }) => {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [tenantdetails, setTenantdetails] = useState({});
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('list');
  const [selectedService, setSelectedService] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [isBackHovered, setIsBackHovered] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [filteredSidebarData, setFilteredSidebarData] = useState([]);
  const [hasScrolledToSelected, setHasScrolledToSelected] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  const sidebarRef = React.useRef(null);
  const scrollTimeout = React.useRef(null);

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'service':
          aValue = a.service || '';
          bValue = b.service || '';
          break;
        case 'category':
          aValue = a.category || '';
          bValue = b.category || '';
          break;
        case 'description':
          aValue = a.description || '';
          bValue = b.description || '';
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // NEW: Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshServices = useCallback(() => {
    setLoading(true);

    InventoryService.GetTenantdetails(currentTenant)
      .then((res) => setTenantdetails(res.data.country))
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          alert("Your Session is Lost: Please login Back to resume");
          navigate("/");
        }
      });

    InventoryService.getServices(currentTenant)
      .then((response) => {
        if (response.data) {
          const sortedServices = response.data.sort((a, b) => (a.id < b.id ? 1 : -1));
          setServices(sortedServices);
          setFilteredServices(sortedServices);
        }
      })
      .catch((error) => {
        console.error("Error retrieving services:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentTenant, navigate]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    setLoading(true);
    refreshServices();

    if (location.state != null) {
      setMessage(location.state.message || "");
    } else {
      setMessage("");
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [location.state, navigate, refreshServices]);

  useEffect(() => {
    updateFilteredData();
  }, [services, searchTerm]);

  useEffect(() => {
    // Auto-scroll in detail view (only once)
    if (currentView === 'detail' && selectedService && sidebarRef.current && !hasScrolledToSelected) {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      scrollTimeout.current = setTimeout(() => {
        const selectedElement = sidebarRef.current.querySelector(`[data-service-id="${selectedService.id}"]`);
        if (selectedElement) {
          selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHasScrolledToSelected(true);
        }
      }, 100);
    }
  }, [currentView, selectedService, hasScrolledToSelected]);

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = services;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = services.filter(service =>
        service.service?.toLowerCase().includes(lowerSearchTerm) ||
        service.servicecode?.toLowerCase().includes(lowerSearchTerm) ||
        service.category?.toLowerCase().includes(lowerSearchTerm) ||
        service.mcategory?.toLowerCase().includes(lowerSearchTerm) ||
        service.description?.toLowerCase().includes(lowerSearchTerm)
      );
    }

    setFilteredServices(filtered);
    if (currentView === 'detail') {
      setFilteredSidebarData(filtered);
    }
    setCurrentPage(1);
  };

  // Get current page data for pagination (with sorting)
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredServices);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const deleteService = useCallback((id) => {
    Swal.fire({
      title: "Are you sure you want to delete this Service?",
      text: "This action cannot be undone.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#4d4d4d",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        InventoryService.deleteService(id)
          .then((response) => {
            setMessage("Service deleted Successfully");
            setLoading(true);
            Swal.fire({
              title: "Service deleted Successfully",
              text: ".",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#4d4d4d",
            }).then(() => {
              InventoryService.getServices(currentTenant).then((response) => {
                const sortedServices = response.data.sort((a, b) => (a.id < b.id ? 1 : -1));
                setServices(sortedServices);
                setFilteredServices(sortedServices);
                setLoading(false);
                if (selectedService && selectedService.id === id) {
                  setCurrentView('list');
                  setSelectedService(null);
                }
              });
            });
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#4d4d4d",
              }).then(() => navigate("/"));
            } else {
              Swal.fire({
                title: "Error occurred while deleting",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#4d4d4d",
              });
            }
          });
      }
    });
  }, [navigate, selectedService, currentTenant]);

  const pdf = useCallback(() => {
    const curdate = moment().format("DD-MM-YYYY HH:mm:ss");
    const columns = [
      { header: "Service", dataKey: "service" },
      { header: "HSN/SAC Code", dataKey: "servicecode" },
      { header: "Service Tax(%)", dataKey: "servicetax" },
      { header: "Category", dataKey: "category" },
      { header: "Service Description", dataKey: "description" }
    ];

    var doc = new jsPDF("p", "pt", [842, 595]);
    autoTable(doc, {
      theme: "grid",
      startY: 120,
      columnStyles: { 7: { halign: "right" } },
      didDrawPage: (HookData) => {
        HookData.settings.margin.top = 120;
      },
      body: services,
      columns: columns,
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageSize = doc.internal.pageSize;
      const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
      const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
      const footer = `Page ${i} of ${pageCount}`;

      doc.addImage(logo, "GIF", 40, 20, 60, 60, { baseline: "top" });
      doc.setFontSize(8);
      doc.text(`${curdate}`, 560, 20, "right");
      doc.setFontSize(16);
      doc.text(`${clientname}`, 180, 40, { baseline: "top" });
      doc.setFontSize(14);
      doc.text("Services", 210, 70, { baseline: "top" });
      doc.line(0, 90, 600, 90);
      doc.setFontSize(10);

      doc.text(
        footer,
        pageWidth / 2 - doc.getTextWidth(footer) / 2,
        pageHeight - 15,
        { baseline: "bottom" }
      );
    }
    doc.save("Services.pdf");
  }, [services]);

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setCurrentView('detail');
    setHasScrolledToSelected(false);
    setFilteredSidebarData(filteredServices);
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedService(null);
    setHasScrolledToSelected(false);
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredServices.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredServices.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredServices.length)} of {filteredServices.length} services
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <TextField
            select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            variant="outlined"
            size="small"
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <MenuItem value={5}>5 per page</MenuItem>
            <MenuItem value={10}>10 per page</MenuItem>
            <MenuItem value={20}>20 per page</MenuItem>
            <MenuItem value={50}>50 per page</MenuItem>
          </TextField>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredServices.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No services found matching your search' : 'No services available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'service' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('service')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Service Name
                    <SortIndicator columnKey="service" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'category' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('category')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Category
                    <SortIndicator columnKey="category" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'description' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('description')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Description
                    <SortIndicator columnKey="description" />
                  </div>
                </th>
                {(inventoryid === "2" || inventoryid === "3") && (
                  <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((service) => (
                <tr
                  key={service.id}
                  className={`UI-table-row ${hoveredRow === service.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''}`}
                  onClick={() => handleServiceClick(service)}
                  onMouseEnter={() => setHoveredRow(service.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  {/* Service Name Cell */}
                  <td
                    className={`UI-td UI-service-name ${hoveredRow === service.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    <Tooltip
                      title="View Details"
                      placement="right"
                      arrow
                      slotProps={{
                        popper: {
                          sx: { pointerEvents: 'none' },
                        },
                      }}
                      open={!isMobile && hoveredRow === service.id && !hoveredAction}
                    >
                      <span>{highlightSearchText(service.service, searchTerm)}</span>
                    </Tooltip>
                  </td>

                  {/* Category Cell */}
                  <td
                    className={`UI-td ${hoveredRow === service.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(service.category, searchTerm)}
                  </td>

                  {/* Description Cell */}
                  <td
                    className={`UI-td UI-description ${hoveredRow === service.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile UI-description-mobile' : ''}`}
                    title={service.description}
                  >
                    {highlightSearchText(service.description || 'No description', searchTerm)}
                  </td>

                  {/* Actions Cell */}
                  {(inventoryid === "2" || inventoryid === "3") && (
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      <div className="UI-action-buttons">
                        {/* Delete Service */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteService(service.id);
                          }}
                          className={`UI-action-button UI-delete-button UI-delete-button-list ${hoveredAction === `delete-${service.id}` ? 'UI-delete-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                          onMouseEnter={() => setHoveredAction(`delete-${service.id}`)}
                          onMouseLeave={() => setHoveredAction(null)}
                        >
                          <Tooltip title="Delete"
                            arrow
                            slotProps={{
                              popper: {
                                sx: { pointerEvents: 'none' },
                              },
                            }}
                            placement="left">
                            <img
                              src={DeleteIcon}
                              alt="Delete"
                              className={`UI-delete-icon ${isMobile ? 'UI-delete-icon-mobile' : ''} ${hoveredAction === `delete-${service.id}` ? 'UI-delete-icon-hover' : ''}`}
                            />
                          </Tooltip>
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  const renderDetailView = () => {
    if (!selectedService) return null;

    return (
      <div className="UI-detail-view">
        {/* Sidebar with service list */}
        <div className="UI-sidebar">
          <div className="UI-sidebar-header">
            <button
              className={`UI-back-button ${isBackHovered ? 'UI-back-button-hover' : ''}`}
              onClick={handleBackToList}
              onMouseEnter={() => setIsBackHovered(true)}
              onMouseLeave={() => setIsBackHovered(false)}
            >
              <i className="fa fa-arrow-left" /> Back
            </button>
          </div>

          <div className="UI-sidebar-list" ref={sidebarRef}>
            {filteredSidebarData.map((service) => (
              <div
                key={service.id}
                data-service-id={service.id}
                className={`UI-sidebar-item ${selectedService.id === service.id ? 'UI-sidebar-item-active' : ''} ${hovered === service.id ? 'UI-sidebar-item-hover' : ''}`}
                onClick={() => setSelectedService(service)}
                onMouseEnter={() => setHovered(service.id)}
                onMouseLeave={() => setHovered(null)}
              >
                <div className={`UI-sidebar-item-name ${hovered === service.id ? 'UI-sidebar-item-name-hover' : ''}`}>
                  {highlightSearchText(service.service, searchTerm)}
                </div>
                <div className={`UI-sidebar-item-code ${hovered === service.id ? 'UI-sidebar-item-code-hover' : ''}`}>
                  {highlightSearchText(service.servicecode, searchTerm)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detail Content */}
        <div className="UI-detail-content">
          <div className="UI-detail-header">
            <div className="UI-detail-title-wrapper">
              <h2 className="UI-detail-title">{highlightSearchText(selectedService.service, searchTerm)}</h2>
              <p className="UI-detail-subtitle">
                Service Code: {highlightSearchText(selectedService.servicecode, searchTerm)}
              </p>
            </div>

            <div className="UI-detail-actions-group">
              {(inventoryid === "2" || inventoryid === "3") && (
                <button
                  onClick={() => deleteService(selectedService.id)}
                  className={`UI-detail-action-button UI-detail-delete-button ${hovered === "delete" ? 'UI-delete-button-hover' : ''}`}
                  onMouseEnter={() => setHovered("delete")}
                  onMouseLeave={() => setHovered(null)}
                >
                  <Tooltip title="Delete Service" arrow slotProps={{
                    popper: {
                      sx: { pointerEvents: 'none' },
                    },
                  }}>
                    <img
                      src={DeleteIcon}
                      alt="Delete"
                      className={`UI-action-icon ${hovered === "delete" ? 'UI-action-icon-hover' : ''}`}
                    />
                  </Tooltip>
                </button>
              )}
            </div>
          </div>

          <div className="UI-detail-grid-two-column">
            {/* Basic Information */}
            <div className="UI-detail-section">
              <h3 className="UI-section-title">Basic Information</h3>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Service Name</span>
                <span className="UI-status-badge UI-status-active">
                  <span className="UI-detail-value">{highlightSearchText(selectedService.service, searchTerm)}</span>
                </span>
              </div>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Service Code</span>
                <span className="UI-detail-value">{highlightSearchText(selectedService.servicecode, searchTerm)}</span>
              </div>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Tax Rate</span>
                <span className="UI-tax-badge">{selectedService.servicetax}%</span>
              </div>
            </div>

            {/* Category Information */}
            <div className="UI-detail-section">
              <h3 className="UI-section-title">Category Information</h3>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Main Category</span>
                <span className="UI-detail-value">{highlightSearchText(selectedService.mcategory, searchTerm)}</span>
              </div>
              {selectedService.categorylvl2 && (
                <div className="UI-detail-row">
                  <span className="UI-detail-label">Sub Category</span>
                  <span className="UI-detail-value">{highlightSearchText(selectedService.categorylvl2, searchTerm)}</span>
                </div>
              )}
              <div className="UI-detail-row">
                <span className="UI-detail-label">Category Level 3</span>
                <span className="UI-category-badge">{highlightSearchText(selectedService.category, searchTerm)}</span>
              </div>
            </div>

            {/* Description */}
            {selectedService.description && (
              <div className="UI-detail-section UI-grid-full-width">
                <h3 className="UI-section-title">Description</h3>
                <p className="UI-description-text">
                  {highlightSearchText(selectedService.description, searchTerm)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
      <div className="UI-card">
        {/* Header */}
        <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
        <Breadcrumb breadcrumbs={[{name:"Home",path:"/inventory"},{name:"Services",path:null}]}/>

          <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
            <div>
              <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                Services
              </h1>
              <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                {currentView === 'detail' && selectedService
                  ? `ID: ${selectedService.id}`
                  : 'Manage your service catalog'
                }
              </p>
            </div>

            <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
              {/* Mobile Search */}
              {isMobile && (
                <input
                  type="text"
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="UI-mobile-search-input"
                />
              )}

              <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                {/* Desktop Search Toggle */}
                {!isMobile && (
                  <div
                    className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                    onClick={() => {
                      if (!showSearch) setShowSearch(true);
                    }}
                    onMouseEnter={() => setHovered("search")}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <i
                      className="fa fa-search UI-search-icon"
                    />
                    {!showSearch && "Search"}
                    {showSearch && (
                      <input
                        type="text"
                        placeholder="Search services..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="UI-search-input"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        onBlur={() => {
                          if (searchTerm.trim() === "") {
                            setShowSearch(false);
                          }
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Export button */}
                <button
                  className={`UI-btn UI-btn-outline ${hovered === "export" ? 'UI-btn-outline-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                  onClick={pdf}
                  onMouseEnter={() => setHovered("export")}
                  onMouseLeave={() => setHovered(null)}
                >
                  <i className="fa fa-file-pdf" /> {isMobile ? "Export" : "Export"}
                </button>

                {/* New Service button */}
                {(inventoryid === "2" || inventoryid === "3") && (
                  <Link
                    to={{ pathname: "/newservices" }}
                    state={{ id: -1, pagename: "New Service" }}
                    className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                    onMouseEnter={() => setHovered("new")}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <i className="fa fa-plus" /> {isMobile ? "New" : "New Service"}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="UI-loading-container">
            {spin}
          </div>
        ) : (
          <>
            {/* Switch between list and detail view */}
            {currentView === 'list'
              ? renderTable()
              : renderDetailView()
            }
          </>
        )}
      </div>
    </div>
  );
};

export default withRouter(ServiceDataTable);